import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { KeyAssignment } from '../types';
import { assignmentsApi } from '../services/api';

interface AssignmentsState {
  assignments: KeyAssignment[];
  loading: boolean;
  error: string | null;
}

const initialState: AssignmentsState = {
  assignments: [],
  loading: false,
  error: null,
};

// Async thunks
export const fetchAssignments = createAsyncThunk(
  'assignments/fetchAssignments',
  async (_, { rejectWithValue }) => {
    try {
      return await assignmentsApi.getAll();
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const fetchAssignmentsByPerson = createAsyncThunk(
  'assignments/fetchAssignmentsByPerson',
  async (personId: string, { rejectWithValue }) => {
    try {
      return await assignmentsApi.getByPerson(personId);
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const fetchAssignmentsByInventory = createAsyncThunk(
  'assignments/fetchAssignmentsByInventory',
  async (inventoryItemId: string, { rejectWithValue }) => {
    try {
      return await assignmentsApi.getByInventory(inventoryItemId);
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const createAssignmentAsync = createAsyncThunk(
  'assignments/createAssignment',
  async (assignment: KeyAssignment, { rejectWithValue }) => {
    try {
      return await assignmentsApi.create(assignment);
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const updateAssignmentAsync = createAsyncThunk(
  'assignments/updateAssignment',
  async (assignment: KeyAssignment, { rejectWithValue }) => {
    try {
      return await assignmentsApi.update(assignment);
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const returnKeyAsync = createAsyncThunk(
  'assignments/returnKey',
  async ({ id, returnedBy, notes }: { id: string; returnedBy: string; notes?: string }, { rejectWithValue }) => {
    try {
      return await assignmentsApi.returnKey(id, returnedBy, notes);
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

const assignmentsSlice = createSlice({
  name: 'assignments',
  initialState,
  reducers: {
    setAssignments: (state, action: PayloadAction<KeyAssignment[]>) => {
      state.assignments = action.payload;
    },
    addAssignment: (state, action: PayloadAction<KeyAssignment>) => {
      state.assignments.push(action.payload);
    },
    updateAssignment: (state, action: PayloadAction<KeyAssignment>) => {
      const index = state.assignments.findIndex(a => a.id === action.payload.id);
      if (index !== -1) {
        state.assignments[index] = action.payload;
      }
    },
    removeAssignment: (state, action: PayloadAction<string>) => {
      state.assignments = state.assignments.filter(a => a.id !== action.payload);
    },
    clearAssignmentsError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch assignments
      .addCase(fetchAssignments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAssignments.fulfilled, (state, action) => {
        state.loading = false;
        state.assignments = action.payload;
      })
      .addCase(fetchAssignments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch by person
      .addCase(fetchAssignmentsByPerson.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAssignmentsByPerson.fulfilled, (state, action) => {
        state.loading = false;
        state.assignments = action.payload;
      })
      .addCase(fetchAssignmentsByPerson.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch by inventory
      .addCase(fetchAssignmentsByInventory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAssignmentsByInventory.fulfilled, (state, action) => {
        state.loading = false;
        state.assignments = action.payload;
      })
      .addCase(fetchAssignmentsByInventory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create assignment
      .addCase(createAssignmentAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAssignmentAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.assignments.push(action.payload);
      })
      .addCase(createAssignmentAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update assignment
      .addCase(updateAssignmentAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAssignmentAsync.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.assignments.findIndex(a => a.id === action.payload.id);
        if (index !== -1) {
          state.assignments[index] = action.payload;
        }
      })
      .addCase(updateAssignmentAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Return key
      .addCase(returnKeyAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(returnKeyAsync.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.assignments.findIndex(a => a.id === action.payload.id);
        if (index !== -1) {
          state.assignments[index] = action.payload;
        }
      })
      .addCase(returnKeyAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setAssignments, addAssignment, updateAssignment, removeAssignment, clearAssignmentsError } = assignmentsSlice.actions;
export default assignmentsSlice.reducer;
